// The frozen interpreter's root: authenticate → fetch resolved schema → render it. Nothing
// here knows what any dashboard contains.
import { useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { api, useShellQuery } from './app/api';
import { useAppDispatch, useAppSelector } from './app/store';
import { realtime } from './realtime/client';
import { useChannels } from './realtime/hooks';
import { DashboardPage } from './interpreter/DashboardPage';
import { LoginPage } from './interpreter/LoginPage';
import { PublicPage } from './interpreter/PublicPage';
import { setShareToken } from './app/api';
import type { Shell } from './app/types';
import { useToast } from './ui/Toasts';

function DashboardRoute({ shell }: { shell: Shell }) {
  const { dashboardId = '' } = useParams();
  if (!shell.dashboards.includes(dashboardId)) {
    return shell.navigation.defaultDashboard ? <Navigate to={`/d/${shell.navigation.defaultDashboard}`} replace /> : <div className="state">No dashboards are available to your role.</div>;
  }
  return <DashboardPage shell={shell} />;
}

function Authenticated({ token }: { token: string }) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { data: shell, error, isLoading } = useShellQuery();
  useEffect(() => {
    realtime.baseMs = shell?.realtime?.reconnectBaseMs ?? 1000;
    realtime.maxMs = shell?.realtime?.reconnectMaxMs ?? 30_000;
    realtime.connect(token);
  }, [token, shell?.realtime]);
  // Live config changes: re-resolve schemas and widget data without a deploy or reload.
  useChannels(['schema:updates'], (e) => {
    dispatch(api.util.invalidateTags(['Schema', 'Widget']));
    const quiet = ['user-prefs', 'stores', 'navigation', '*'];
    if (e.event === 'schema.updated' && e.payload?.domain && !quiet.includes(e.payload.domain)) toast(`Configuration updated (${e.payload.domain}${e.payload.key ? `/${e.payload.key}` : ''}) — view refreshed`);
  });

  if (isLoading) return <div className="boot"><div className="state">Loading your workspace…</div></div>;
  if (error || !shell) return <div className="boot"><div className="state"><strong>Could not load the workspace</strong>Try signing in again.</div></div>;
  const home = shell.navigation.defaultDashboard;
  return (
    <Routes>
      <Route path="/d/:dashboardId" element={<DashboardRoute shell={shell} />} />
      <Route path="*" element={home ? <Navigate to={`/d/${home}`} replace /> : <div className="state">No dashboards are available to your role.</div>} />
    </Routes>
  );
}

// A public read-only link works without signing in, and never with the visitor's own session.
const shared = window.location.pathname.match(/^\/s\/([A-Za-z0-9_-]{20,64})\/?$/);
if (shared) setShareToken(shared[1]);

export function App() {
  const token = useAppSelector((s) => s.auth.token);
  if (shared) return <PublicPage />;
  if (!token) return <LoginPage />;
  return <Authenticated token={token} />;
}
